import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Plus, Edit2, Pause, Play, Trash2, TrendingUp, Users, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminGrowthPackagesPage = () => {
  const [packages, setPackages] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    short_description: '',
    long_description: '',
    icon: '💎',
    badge: '',
    category: 'starter',
    currency: 'ZAR',
    price: 100,
    duration_days: 10,
    daily_payout: 15,
    total_earnings: null, // null = auto-calculate, or set custom value
    withdrawal_fee_percentage: 5,
    min_withdrawal_amount: 50,
    max_withdrawal_amount: null,
    max_packages_per_user: null,
    unlimited_packages: false,
    auto_activate: true,
    requires_manual_approval: false,
    is_visible: true
  });

  useEffect(() => {
    fetchPackages();
    fetchAnalytics();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/growth-packages`, {
        credentials: 'include'
      });
      const data = await response.json();
      setPackages(data.packages || []);
    } catch (error) {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/growth-packages/analytics/dashboard`, {
        credentials: 'include'
      });
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load analytics');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingPackage 
        ? `${API_URL}/api/admin/growth-packages/${editingPackage.id}`
        : `${API_URL}/api/admin/growth-packages`;
      
      const method = editingPackage ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save package');

      toast.success(editingPackage ? 'Package updated' : 'Package created');
      setShowModal(false);
      resetForm();
      fetchPackages();
      fetchAnalytics();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handlePublish = async (packageId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/growth-packages/${packageId}/publish`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to publish');
      
      toast.success('Package published');
      fetchPackages();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handlePause = async (packageId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/growth-packages/${packageId}/pause`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to pause');
      
      toast.success('Package paused');
      fetchPackages();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleUnpause = async (packageId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/growth-packages/${packageId}/unpause`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to unpause');
      
      toast.success('Package activated');
      fetchPackages();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      short_description: '',
      long_description: '',
      icon: '💎',
      badge: '',
      category: 'starter',
      currency: 'ZAR',
      price: 100,
      duration_days: 10,
      daily_payout: 15,
      withdrawal_fee_percentage: 5,
      min_withdrawal_amount: 50,
      max_withdrawal_amount: null,
      max_packages_per_user: null,
      unlimited_packages: false,
      auto_activate: true,
      requires_manual_approval: false,
      is_visible: true
    });
    setEditingPackage(null);
  };

  // Use custom total_earnings if provided, otherwise auto-calculate
  const totalEarnings = formData.total_earnings !== null && formData.total_earnings !== undefined 
    ? formData.total_earnings 
    : (formData.daily_payout * formData.duration_days);
  const roi = ((totalEarnings - formData.price) / formData.price) * 100;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Growth Packages</h1>
            <p className="text-muted-foreground">Manage fixed daily payout packages</p>
          </div>
          <Button onClick={() => { resetForm(); setShowModal(true); }} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Create Package
          </Button>
        </div>

        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Sales</p>
                    <p className="text-2xl font-bold">R{analytics.total_sales.toFixed(2)}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active Packages</p>
                    <p className="text-2xl font-bold">{analytics.active_user_packages}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Purchases</p>
                    <p className="text-2xl font-bold">{analytics.total_purchases}</p>
                  </div>
                  <Users className="h-8 w-8 text-purple-500 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Earnings Paid</p>
                    <p className="text-2xl font-bold">R{analytics.total_earnings_paid.toFixed(2)}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-orange-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Packages List */}
        <Card>
          <CardHeader>
            <CardTitle>All Packages ({packages.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : packages.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No packages yet. Create your first growth package!
              </div>
            ) : (
              <div className="space-y-3">
                {packages.map((pkg) => (
                  <div key={pkg.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50">
                    <div className="flex items-center gap-4">
                      <div className="text-4xl">{pkg.icon}</div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-lg">{pkg.name}</span>
                          <Badge variant={pkg.status === 'active' ? 'default' : 'secondary'}>
                            {pkg.status}
                          </Badge>
                          {pkg.badge && <Badge variant="outline">{pkg.badge}</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground">{pkg.short_description}</p>
                        <div className="text-xs text-muted-foreground mt-1 flex gap-4">
                          <span>Price: R{pkg.price}</span>
                          <span>Daily: R{pkg.daily_payout}</span>
                          <span>Duration: {pkg.duration_days} days</span>
                          <span>Total: R{pkg.total_earnings}</span>
                          <span>ROI: {pkg.roi_percentage.toFixed(1)}%</span>
                          <span>Purchases: {pkg.purchases_count}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {pkg.status === 'draft' && (
                        <Button size="sm" onClick={() => handlePublish(pkg.id)}>
                          <Play className="h-4 w-4 mr-1" />
                          Publish
                        </Button>
                      )}
                      {pkg.status === 'active' && (
                        <Button size="sm" variant="outline" onClick={() => handlePause(pkg.id)}>
                          <Pause className="h-4 w-4 mr-1" />
                          Pause
                        </Button>
                      )}
                      {pkg.status === 'paused' && (
                        <Button size="sm" variant="default" onClick={() => handleUnpause(pkg.id)}>
                          <Play className="h-4 w-4 mr-1" />
                          Activate
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => { setEditingPackage(pkg); setFormData({...pkg}); setShowModal(true); }}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle>{editingPackage ? 'Edit Package' : 'Create New Package'}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Package Name *</Label>
                      <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Icon</Label>
                      <Input value={formData.icon} onChange={(e) => setFormData({...formData, icon: e.target.value})} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Short Description *</Label>
                    <Input value={formData.short_description} onChange={(e) => setFormData({...formData, short_description: e.target.value})} required />
                  </div>

                  <div className="space-y-2">
                    <Label>Long Description</Label>
                    <textarea value={formData.long_description} onChange={(e) => setFormData({...formData, long_description: e.target.value})} className="w-full border rounded-md p-2 min-h-[80px]" />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Category *</Label>
                      <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full border rounded-md p-2">
                        <option value="starter">Starter</option>
                        <option value="bronze">Bronze</option>
                        <option value="silver">Silver</option>
                        <option value="gold">Gold</option>
                        <option value="platinum">Platinum</option>
                        <option value="vip">VIP</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label>Badge</Label>
                      <select value={formData.badge} onChange={(e) => setFormData({...formData, badge: e.target.value})} className="w-full border rounded-md p-2">
                        <option value="">None</option>
                        <option value="new">New</option>
                        <option value="popular">Popular</option>
                        <option value="premium">Premium</option>
                        <option value="limited">Limited</option>
                        <option value="hot">Hot</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label>Currency *</Label>
                      <select value={formData.currency} onChange={(e) => setFormData({...formData, currency: e.target.value})} className="w-full border rounded-md p-2">
                        <option value="ZAR">ZAR</option>
                        <option value="USD">USD</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Price (R) *</Label>
                      <Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})} required />
                    </div>

                    <div className="space-y-2">
                      <Label>Duration (Days) *</Label>
                      <Input type="number" value={formData.duration_days} onChange={(e) => setFormData({...formData, duration_days: parseInt(e.target.value)})} required />
                    </div>

                    <div className="space-y-2">
                      <Label>Daily Payout (R) *</Label>
                      <Input type="number" step="0.01" value={formData.daily_payout} onChange={(e) => setFormData({...formData, daily_payout: parseFloat(e.target.value)})} required />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Total Earnings (R) - Optional</Label>
                      <Input 
                        type="number" 
                        step="0.01" 
                        value={formData.total_earnings || ''} 
                        onChange={(e) => setFormData({...formData, total_earnings: e.target.value ? parseFloat(e.target.value) : null})} 
                        placeholder="Auto: Daily Payout × Duration"
                      />
                      <p className="text-xs text-muted-foreground">Leave empty to auto-calculate from daily payout × duration</p>
                    </div>
                  </div>

                  <Card className="bg-muted/50">
                    <CardContent className="pt-4">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-sm text-muted-foreground">Total Earnings</p>
                          <p className="text-xl font-bold">R{totalEarnings.toFixed(2)}</p>
                          <p className="text-xs text-muted-foreground">{formData.total_earnings ? 'Custom' : 'Auto-calculated'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">ROI</p>
                          <p className="text-xl font-bold text-green-600">{roi.toFixed(1)}%</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Net Profit</p>
                          <p className="text-xl font-bold">R{(totalEarnings - formData.price).toFixed(2)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Withdrawal Fee (%)</Label>
                      <Input type="number" step="0.1" value={formData.withdrawal_fee_percentage} onChange={(e) => setFormData({...formData, withdrawal_fee_percentage: parseFloat(e.target.value)})} />
                    </div>

                    <div className="space-y-2">
                      <Label>Min Withdrawal (R)</Label>
                      <Input type="number" step="0.01" value={formData.min_withdrawal_amount} onChange={(e) => setFormData({...formData, min_withdrawal_amount: parseFloat(e.target.value)})} />
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="auto_activate" checked={formData.auto_activate} onChange={(e) => setFormData({...formData, auto_activate: e.target.checked})} className="w-4 h-4" />
                      <Label htmlFor="auto_activate" className="cursor-pointer">Auto Activate</Label>
                    </div>

                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="is_visible" checked={formData.is_visible} onChange={(e) => setFormData({...formData, is_visible: e.target.checked})} className="w-4 h-4" />
                      <Label htmlFor="is_visible" className="cursor-pointer">Visible to Users</Label>
                    </div>

                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="unlimited" checked={formData.unlimited_packages} onChange={(e) => setFormData({...formData, unlimited_packages: e.target.checked})} className="w-4 h-4" />
                      <Label htmlFor="unlimited" className="cursor-pointer">Unlimited Purchases</Label>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4 border-t">
                    <Button type="submit" className="flex-1">
                      {editingPackage ? 'Update Package' : 'Create Package'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1">
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
